/**
 * Schema inference over untyped, nested records.
 *
 * Plain objects are flattened into dotted-path leaf columns grouped under their parent
 * path; arrays stay single columns (never exploded into rows), with element paths
 * recorded so filters and statistics can reach inside them.
 */

import { CATEGORICAL_MAX_DISTINCT, inferColumnType, valueKind } from './infer';
import type {
  ColumnDef,
  ColumnGroup,
  GetElementValues,
  GetGroupValue,
  GetValue,
  InferSchema,
  Row,
  ValueKind,
} from './types';

/** How deep element paths go inside arrays of objects (spec assumption). */
const MAX_ELEMENT_DEPTH = 3;

export function isMissing(v: unknown): boolean {
  return v === undefined || v === null;
}

export function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function walkPath(root: unknown, path: string[]): unknown {
  let cur: unknown = root;
  for (const seg of path) {
    if (!isPlainObject(cur)) return undefined;
    cur = cur[seg];
  }
  return cur;
}

export const getValue: GetValue = (row, column) => walkPath(row, column.path);

export const getGroupValue: GetGroupValue = (row, group) => walkPath(row, group.id.split('.'));

/**
 * Values at `elementPath` across every element of an array. "" is the element itself;
 * dotted segments walk objects; a segment ending in "[]" flattens a nested array
 * ("properties[].type", "aliases[]").
 */
export const getElementValues: GetElementValues = (arrayValue, elementPath) => {
  if (!Array.isArray(arrayValue)) return [];
  let values: unknown[] = arrayValue;
  if (elementPath === '') return values;
  for (const token of elementPath.split('.')) {
    const flat = token.endsWith('[]');
    const key = flat ? token.slice(0, -2) : token;
    const next: unknown[] = [];
    for (const v of values) {
      const w = isPlainObject(v) ? v[key] : undefined;
      if (!flat) next.push(w);
      else if (Array.isArray(w)) next.push(...w);
    }
    values = next;
  }
  return values;
};

/** Collect element paths from a sampled array element. */
function collectElementPaths(el: unknown, prefix: string, depth: number, into: Set<string>): void {
  if (Array.isArray(el)) {
    // Arrays of arrays at the top, or past the depth limit: address the value as-is.
    if (!prefix || depth >= MAX_ELEMENT_DEPTH) {
      into.add(prefix);
      return;
    }
    for (const child of el.slice(0, 5)) collectElementPaths(child, `${prefix}[]`, depth + 1, into);
    return;
  }
  if (isPlainObject(el)) {
    for (const [k, v] of Object.entries(el)) {
      collectElementPaths(v, prefix ? `${prefix}.${k}` : k, depth, into);
    }
    return;
  }
  into.add(prefix);
}

interface Acc {
  path: string[];
  firstSeen: number;
  values: unknown[];
  typeCounts: Partial<Record<ValueKind, number>>;
  elementPaths: Set<string>;
}

export const inferSchema: InferSchema = (rows: Row[]) => {
  const leaves = new Map<string, Acc>();
  const groupOrder = new Map<string, number>();
  let seq = 0;

  const touch = (id: string, path: string[]): Acc => {
    let acc = leaves.get(id);
    if (!acc) {
      acc = { path, firstSeen: seq++, values: [], typeCounts: {}, elementPaths: new Set() };
      leaves.set(id, acc);
    }
    return acc;
  };

  const visit = (obj: Record<string, unknown>, prefix: string[]) => {
    for (const [key, value] of Object.entries(obj)) {
      const path = [...prefix, key];
      const id = path.join('.');
      if (isPlainObject(value) && Object.keys(value).length > 0) {
        if (!groupOrder.has(id)) groupOrder.set(id, seq++);
        // A path that is an object here but a scalar elsewhere still gets its leaf
        // (typed mixed) from the scalar rows; the object rows contribute children.
        if (leaves.has(id)) {
          const acc = leaves.get(id)!;
          acc.values.push(value);
          acc.typeCounts.object = (acc.typeCounts.object ?? 0) + 1;
        }
        visit(value, path);
        continue;
      }
      const acc = touch(id, path);
      if (isMissing(value)) continue;
      acc.values.push(value);
      const kind = valueKind(value);
      acc.typeCounts[kind] = (acc.typeCounts[kind] ?? 0) + 1;
      if (Array.isArray(value)) {
        // Sampling the first few elements per row is plenty to discover structure.
        for (const el of value.slice(0, 5)) collectElementPaths(el, '', 0, acc.elementPaths);
      }
    }
  };

  rows.forEach((row) => {
    if (isPlainObject(row)) visit(row, []);
  });

  // A path seen as `{}` in some rows and as a populated object in others is a group, not a
  // column: drop the placeholder leaf so its values flatten into the child columns (the
  // empty rows then read as missing there). A leaf that also holds scalars stays, as mixed.
  for (const [id, acc] of leaves) {
    const kinds = Object.keys(acc.typeCounts);
    if (groupOrder.has(id) && kinds.every((k) => k === 'object')) leaves.delete(id);
  }

  const columns: ColumnDef[] = [...leaves.entries()]
    .map(([id, acc]) => {
      const name = acc.path[acc.path.length - 1];
      const type = inferColumnType(acc.values, name, acc.typeCounts);
      let categorical = false;
      if (type === 'text' || type === 'boolean') {
        const distinct = new Set(acc.values.map((v) => String(v)));
        categorical = distinct.size <= CATEGORICAL_MAX_DISTINCT;
      }
      return {
        id,
        path: acc.path,
        name,
        group: acc.path.length > 1 ? acc.path.slice(0, -1).join('.') : null,
        type,
        typeCounts: acc.typeCounts,
        categorical,
        elementPaths: type === 'array' ? [...acc.elementPaths].sort(byDepthThenName) : [],
        firstSeen: acc.firstSeen,
      };
    })
    .sort((a, b) => a.firstSeen - b.firstSeen);

  // Groups: every object path that has at least one descendant leaf.
  const groupMap = new Map<string, ColumnGroup>();
  const ensureGroup = (id: string): ColumnGroup => {
    let g = groupMap.get(id);
    if (!g) {
      const segs = id.split('.');
      g = {
        id,
        name: segs[segs.length - 1],
        parent: segs.length > 1 ? segs.slice(0, -1).join('.') : null,
        children: [],
        columnIds: [],
      };
      groupMap.set(id, g);
    }
    return g;
  };
  for (const col of columns) {
    for (let i = 1; i < col.path.length; i++) {
      ensureGroup(col.path.slice(0, i).join('.')).columnIds.push(col.id);
    }
  }
  // Direct children in first-seen order (columns and subgroups interleaved).
  const firstSeenOf = (id: string) => leaves.get(id)?.firstSeen ?? groupOrder.get(id) ?? Infinity;
  for (const g of groupMap.values()) {
    const depth = g.id.split('.').length;
    const direct = new Set<string>();
    for (const cid of g.columnIds) direct.add(cid.split('.').slice(0, depth + 1).join('.'));
    g.children = [...direct].sort((a, b) => firstSeenOf(a) - firstSeenOf(b));
  }
  const groups = [...groupMap.values()].sort((a, b) => firstSeenOf(a.id) - firstSeenOf(b.id));

  return {
    columns,
    groups,
    byId: new Map(columns.map((c) => [c.id, c])),
    groupById: new Map(groups.map((g) => [g.id, g])),
  };
};

function byDepthThenName(a: string, b: string): number {
  const da = a.split(/\.|\[\]/).length;
  const db = b.split(/\.|\[\]/).length;
  return da - db || a.localeCompare(b);
}
